import { defineComponent as _defineComponent } from 'vue';
import { resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createSlots as _createSlots, unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "row q-ma-md justify-end" };
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { LOCALE_DATETIME_FORMAT } from '@models/timeformats.js';
import date from 'quasar/src/utils/date/date.js';
import { useAuthStore } from '@stores/auth';
export default /*@__PURE__*/ _defineComponent({
    __name: 'DatePicker',
    props: {
        dense: { type: Boolean, default: false },
        noIcon: { type: Boolean, default: false },
        errorMessage: { default: '' },
        rules: { default: () => [] },
        label: { default: '' },
        modelValue: { default: '' },
        disable: { type: Boolean, default: false },
        dateRange: { default: () => ({
                start: null,
                end: null
            }) },
        readonly: { type: Boolean, default: false },
        enableValidationRules: { type: Boolean, default: true },
        allowEmpty: { type: Boolean, default: false },
        defaultToNow: { type: Boolean, default: false }
    },
    emits: ["update:modelValue"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const authStore = useAuthStore();
        // State
        const showPicker = ref(false);
        const internalDate = ref(null);
        const isUpdating = ref(false);
        // Date picker masks - extract only date portion
        const pickerMasks = Object.fromEntries(Object.entries(LOCALE_DATETIME_FORMAT).map(([key, value]) => [
            key,
            value.split(' ')[0]
        ]));
        // Computed properties
        const userLocale = computed(() => authStore.getUserLocale);
        const pickerMask = computed(() => pickerMasks[userLocale.value]);
        const selectedDate = computed({
            get() {
                return internalDate.value;
            },
            set(value) {
                internalDate.value = value;
                updateModelValue(value);
            }
        });
        // Methods
        /**
         * Sync internal state from modelValue prop.
         */
        const syncFromModelValue = () => {
            const value = props.modelValue;
            if (value && value.length > 0) {
                if (isValidISO(value)) {
                    internalDate.value = fromISOString(value);
                }
                else {
                    internalDate.value = value;
                }
            }
            else {
                internalDate.value = null;
            }
        };
        /**
         * Update modelValue from internal date.
         */
        const updateModelValue = (value) => {
            if (isUpdating.value)
                return;
            isUpdating.value = true;
            try {
                if (value && isValidDateFormat(value)) {
                    const isoDate = toISOString(value);
                    if (props.modelValue !== isoDate) {
                        emit('update:modelValue', isoDate);
                    }
                }
                else if (!value || value === '') {
                    if (props.modelValue !== '') {
                        emit('update:modelValue', '');
                    }
                }
            }
            catch (error) {
                console.error('DatePicker: Failed to update model value', error);
            }
            finally {
                nextTick(() => {
                    isUpdating.value = false;
                });
            }
        };
        /**
         * Handle cancel.
         */
        const cancel = () => {
            showPicker.value = false;
        };
        /**
         * Convert server format (ISO) to picker format.
         * @param serverDate - Date in ISO format (YYYY-MM-DD or full ISO datetime).
         * @return Date in picker format (YYYY/MM/DD).
         */
        const fromISOString = (serverDate) => {
            // Handle both YYYY-MM-DD and full ISO datetime strings
            const dateOnly = serverDate.split('T')[0];
            return date.formatDate(dateOnly, pickerMask.value);
        };
        /**
         * Convert picker format to ISO date string.
         * @param pickerDate - Date in picker format (YYYY/MM/DD).
         * @return Date in ISO format (YYYY-MM-DD).
         * @throws Error if date is invalid or empty.
         */
        const toISOString = (pickerDate) => {
            // Input validation
            if (!pickerDate || typeof pickerDate !== 'string') {
                throw new Error('Invalid date input: must be non-empty string');
            }
            // Validate date format
            if (!isValidDateFormat(pickerDate)) {
                throw new Error('Invalid date format: expected YYYY/MM/DD');
            }
            // Parse date components
            const [year, month, day] = pickerDate.split('/');
            // Validate parsed components
            if (!year || !month || !day) {
                throw new Error('Invalid date: unable to parse components');
            }
            // Return ISO date string (YYYY-MM-DD)
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        };
        /**
         * Determine whether a given date should be selectable.
         * @param dateStr - Date string to check.
         * @return True if selectable, false otherwise.
         */
        const selectableDateRange = (dateStr) => {
            if (props.dateRange.start || props.dateRange.end) {
                const dateObj = new Date(dateStr);
                const start = new Date(props.dateRange.start).setHours(0, 0, 0, 0);
                const end = props.dateRange.end
                    ? new Date(props.dateRange.end).setHours(0, 0, 0, 0)
                    : new Date().getTime();
                return dateObj.getTime() >= start && dateObj.getTime() <= end;
            }
            else {
                // allow any date to be selected
                return true;
            }
        };
        /**
         * Handle date selection.
         */
        const select = () => {
            if (internalDate.value) {
                updateModelValue(internalDate.value);
                showPicker.value = false;
            }
        };
        /**
         * Validate date format (YYYY/MM/DD).
         * @param dateStr - The date string to validate.
         * @return True if the date format is valid, false otherwise.
         */
        const isValidDateFormat = (dateStr) => {
            if (dateStr) {
                const dateRegEx = /^\d{4}([/])\d{2}\1\d{2}$/;
                if (dateStr.match(dateRegEx)) {
                    // Additional validation: check if it's a valid date
                    const dateObj = new Date(dateStr);
                    return !isNaN(dateObj.getTime());
                }
            }
            return false;
        };
        /**
         * Validate if string is in ISO format.
         * @param string - The string to validate.
         * @return True if the string matches ISO format, false otherwise.
         */
        const isValidISO = (string) => {
            if (!string) {
                return false;
            }
            // Match both YYYY-MM-DD and full ISO datetime strings
            const isoRegEx = /^\d{4}-\d{2}-\d{2}(T.*)?$/;
            return !!string.match(isoRegEx);
        };
        /**
         * Validation rule for the input field.
         * @param val - The current input value to validate.
         * @return True if valid, error message otherwise.
         */
        const isValidDate = (val) => {
            if (!props.enableValidationRules) {
                return true;
            }
            if (!val) {
                return props.allowEmpty
                    ? true
                    : t('Please enter valid date format (YYYY/MM/DD).');
            }
            if (isValidDateFormat(val)) {
                return true;
            }
            else {
                return t('Please enter valid date format (YYYY/MM/DD).');
            }
        };
        // Lifecycle
        onMounted(() => {
            syncFromModelValue();
        });
        // Watchers
        watch(() => props.modelValue, () => {
            if (!isUpdating.value) {
                syncFromModelValue();
            }
        });
        watch(showPicker, isOpen => {
            // Auto-initialize with current date when picker opens
            if (isOpen &&
                props.defaultToNow &&
                (!internalDate.value || internalDate.value === '')) {
                const now = new Date();
                internalDate.value = date.formatDate(now, pickerMask.value);
            }
        });
        return (_ctx, _cache) => {
            const _component_q_icon = _resolveComponent("q-icon");
            const _component_q_input = _resolveComponent("q-input");
            const _component_q_btn = _resolveComponent("q-btn");
            const _component_q_date = _resolveComponent("q-date");
            const _component_q_dialog = _resolveComponent("q-dialog");
            return (_openBlock(), _createElementBlock("div", null, [
                _createVNode(_component_q_input, {
                    onClick: _cache[1] || (_cache[1] = ($event) => (__props.disable || _ctx.$q.screen.gt.sm ? () => { } : (showPicker.value = true))),
                    modelValue: selectedDate.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((selectedDate).value = $event)),
                    label: __props.label,
                    dense: __props.dense,
                    error: __props.errorMessage !== '',
                    "error-message": __props.errorMessage,
                    rules: [isValidDate, ...__props.rules],
                    disable: __props.disable,
                    readonly: _ctx.$q.screen.lt.sm ? true : __props.readonly,
                    class: "date-picker-input",
                    outlined: "",
                    style: _normalizeStyle(`padding-bottom: ${_ctx.$q.screen.lt.md ? '0px' : '20px'}`),
                    "bg-color": "white",
                    color: "accent",
                    mask: "####/##/##"
                }, _createSlots({ _: 2 }, [
                    (!__props.noIcon)
                        ? {
                            name: "prepend",
                            fn: _withCtx(() => [
                                _createVNode(_component_q_icon, {
                                    onClick: _cache[0] || (_cache[0] = ($event) => (showPicker.value = true)),
                                    name: "event",
                                    class: "cursor-pointer"
                                })
                            ]),
                            key: "0"
                        }
                        : undefined
                ]), 1032, ["modelValue", "label", "dense", "error", "error-message", "rules", "disable", "readonly", "style"]),
                _createVNode(_component_q_dialog, {
                    modelValue: showPicker.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((showPicker).value = $event))
                }, {
                    default: _withCtx(() => [
                        _createVNode(_component_q_date, {
                            modelValue: selectedDate.value,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((selectedDate).value = $event)),
                            mask: _unref(pickerMasks)[userLocale.value],
                            options: selectableDateRange,
                            class: "date-picker-dialog"
                        }, {
                            default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_1, [
                                    _createVNode(_component_q_btn, {
                                        onClick: cancel,
                                        label: _ctx.$t('Cancel'),
                                        class: "q-mr-sm",
                                        color: "white",
                                        "text-color": "primary",
                                        outline: ""
                                    }, null, 8, ["label"]),
                                    _createVNode(_component_q_btn, {
                                        onClick: select,
                                        label: _ctx.$t('Select'),
                                        color: "primary",
                                        disable: !selectedDate.value
                                    }, null, 8, ["label", "disable"])
                                ])
                            ]),
                            _: 1
                        }, 8, ["modelValue", "mask"])
                    ]),
                    _: 1
                }, 8, ["modelValue"])
            ]));
        };
    }
});
